import React from 'react';
import dayjs from 'dayjs';

export type EventTeaserProps = {
  labels: {
    topline: string;
  };

  endDate?: number;
  startDate: number;
  format?: string[];
  hint?: string;
  title: string;
  copy?: string;
  categories?: string[];
  href: string;
};

const EventTeaser = ({
  labels,
  startDate,
  endDate,
  format,
  hint,
  title,
  copy,
  categories,
  href
}: EventTeaserProps) => {
  const getStartDate = () => dayjs(startDate * 1000).format('DD.MM.');

  const getYear = () => {
    const startYear = dayjs(startDate * 1000).format('YYYY');
    if (!endDate) return startYear;

    const endYear = dayjs(endDate * 1000).format('YYYY');
    if (startYear !== endYear) return `${startYear} – ${endYear}`;
    return startYear;
  };

  const getEndDate = () => {
    if (!endDate) return;
    const formatted = dayjs(endDate * 1000).format('DD.MM.');
    if (formatted === getStartDate()) return;
    return formatted;
  };

  const getFullDate = () => {
    if (!getEndDate()) return `${getStartDate()}${getYear()}`;
    return `${getStartDate()} – ${getEndDate()}${getYear()}`;
  };

  return (
    <a className="event-teaser" title={title} href={href}>
      <div className="event-teaser__date event-teaser__date--desktop">
        {labels.topline && getEndDate() && (
          <span className="typo-button font-medium">{labels.topline}</span>
        )}

        <span className="typo-headline-4 font-bold">{getStartDate()}</span>

        {getEndDate() && (
          <>
            <span className="event-teaser__date-spacer typo-headline-3">–</span>
            <span className="typo-headline-4 font-bold">{getEndDate()}</span>
          </>
        )}

        <span className="typo-button font-bold">{getYear()}</span>
      </div>

      <div className="event-teaser__date event-teaser__date--mobile">
        {labels.topline && getEndDate() && (
          <span className="typo-button font-medium">{labels.topline}</span>
        )}
        <span className="typo-headline-4 font-bold">{getFullDate()}</span>
      </div>

      <div className="event-teaser__content">
        <div className="event-teaser__topline">
          {format && format.length > 0 && (
            <p className="event-teaser__format typo-button font-bold">
              {format.join(' | ')}
            </p>
          )}

          {hint && (
            <p className="event-teaser__hint typo-button font-bold">{hint}</p>
          )}
        </div>

        <h2 className="typo-headline-3 font-bold">{title}</h2>

        {copy && (
          <div
            className="typo-copy"
            dangerouslySetInnerHTML={{ __html: copy }}
          />
        )}

        {categories && (
          <p className="event-teaser__categories typo-button font-medium">
            {categories?.join(' | ')}
          </p>
        )}
      </div>

      <div className="event-teaser__meta">
        <div className="event-teaser__icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 32 32"
          >
            <path
              fill="currentColor"
              fillRule="nonzero"
              d="m16 7-1.586 1.586 6.277 6.289H7v2.25h13.691l-6.277 6.289L16 25l9-9z"
            />
          </svg>
        </div>
      </div>
    </a>
  );
};

export default EventTeaser;
